import React, { useState } from 'react';
import { X, Mail, Lock, User, ShieldCheck, ArrowRight, CheckCircle2, AlertCircle, RefreshCw } from 'lucide-react';
import { authService } from '../../services/auth/authService';
import { AppUserSession } from '../../types';

interface UserAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSessionUpdated: (session: AppUserSession) => void;
}

export const UserAuthModal: React.FC<UserAuthModalProps> = ({ isOpen, onClose, onSessionUpdated }) => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);

    try {
      setTimeout(() => {
        if (mode === 'login') {
          const session = authService.login(email || 'harryeliechama@gmail.com', password || 'securePass123');
          onSessionUpdated(session);
          setSuccessMessage('Connexion réussie ! Bienvenue sur YamooPay.');
          setTimeout(() => {
            setIsLoading(false);
            onClose();
          }, 800);
        } else if (mode === 'signup') {
          const session = authService.signup(fullName || 'Utilisateur YamooPay', email, password);
          onSessionUpdated(session);
          setSuccessMessage('Compte créé avec succès ! Bienvenue.');
          setTimeout(() => {
            setIsLoading(false);
            onClose();
          }, 800);
        } else if (mode === 'forgot') {
          authService.requestPasswordReset(email);
          setIsLoading(false);
          setSuccessMessage('Un e-mail de réinitialisation de mot de passe a été envoyé.');
        }
      }, 500);
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Une erreur est survenue.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
      <div className="bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] w-full max-w-md overflow-hidden relative">
        {/* Header */}
        <div className="bg-[#0E7044] px-6 py-5 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center font-black text-lg">
              YP
            </div>
            <div>
              <h3 className="font-bold text-lg">YamooPay Authentification</h3>
              <p className="text-xs text-emerald-100">Espace Sécurisé & Calculs Personnalisés</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors cursor-pointer"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-[#0E7044] text-sm">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">
                  Nom Complet
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="text"
                    required
                    placeholder="ex. Harry Elie Chama"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A]"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-1.5">
                Adresse E-mail Professionnelle
              </label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="email"
                  required
                  placeholder="nom@yamoopay.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A]"
                />
              </div>
            </div>

            {mode !== 'forgot' && (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-[#4B5563]">
                    Mot de passe
                  </label>
                  {mode === 'login' && (
                    <button
                      type="button"
                      onClick={() => setMode('forgot')}
                      className="text-xs font-medium text-[#0E7044] hover:underline"
                    >
                      Mot de passe oublié ?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-3.5 w-4 h-4 text-[#9CA3AF]" />
                  <input
                    type="password"
                    required
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#0E7044] text-sm text-[#1A1A1A]"
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-[#0E7044] hover:bg-[#0B5C37] text-white font-bold rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>
                    {mode === 'login' ? 'Se connecter' : mode === 'signup' ? "S'inscrire sur YamooPay" : 'Envoyer le lien de réinitialisation'}
                  </span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          {/* Mode Switchers */}
          <div className="mt-6 pt-4 border-t border-[#E2E8F0] text-center text-sm text-[#4B5563]">
            {mode === 'login' ? (
              <p>
                Pas encore de compte ?{' '}
                <button
                  onClick={() => {
                    setMode('signup');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="font-bold text-[#0E7044] hover:underline cursor-pointer"
                >
                  Créer un compte
                </button>
              </p>
            ) : mode === 'signup' ? (
              <p>
                Déjà un compte ?{' '}
                <button
                  onClick={() => {
                    setMode('login');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="font-bold text-[#0E7044] hover:underline cursor-pointer"
                >
                  Se connecter
                </button>
              </p>
            ) : (
              <p>
                <button
                  onClick={() => {
                    setMode('login');
                    setError(null);
                    setSuccessMessage(null);
                  }}
                  className="font-bold text-[#0E7044] hover:underline cursor-pointer"
                >
                  Retour à la connexion
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
